import { siteLocale } from '@sosoapi/browser-preferences/locales'

/** Only a short-lived claim attempt is retained in this tab. Never store the
 * agent's claim token, user code, access token or renewable assertion here. */
export const CLAIM_ISOLATION_KEY = 'sosoapi.agent-claim-isolated.v1'
export const CLAIM_CONTINUATION_KEY = 'sosoapi.agent-claim.v1'
export const CLAIM_ATTEMPT_TTL_MS = 5 * 60 * 1000
const ATTEMPT = /^[A-Za-z0-9_-]{43,128}$/
const AUTH_PATHS = new Set([
  '/signin',
  '/signup',
  '/auth/callback',
  '/auth/error',
  '/forgot-password',
  '/reset-password',
])
export type ClaimContinuation = { attemptToken: string; expiresAt: number }
type StoragePort = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>
const unavailableStorage: StoragePort = {
  getItem: () => null,
  setItem: () => {},
  removeItem: () => {},
}
function currentStorage(): StoragePort {
  try {
    return sessionStorage
  } catch {
    return unavailableStorage
  }
}
type ClaimWindow = {
  location: Pick<Location, 'pathname' | 'href'>
  history: Pick<History, 'replaceState'>
  sessionStorage: StoragePort
  document: Pick<Document, 'querySelector' | 'createElement' | 'head'>
}

export function readClaimContinuation(
  storage: StoragePort = currentStorage(),
  now = Date.now(),
): ClaimContinuation | null {
  try {
    const raw = storage.getItem(CLAIM_CONTINUATION_KEY)
    const value = raw ? (JSON.parse(raw) as Partial<ClaimContinuation>) : null
    if (
      value &&
      typeof value.attemptToken === 'string' &&
      ATTEMPT.test(value.attemptToken) &&
      typeof value.expiresAt === 'number' &&
      value.expiresAt > now &&
      value.expiresAt <= now + CLAIM_ATTEMPT_TTL_MS
    )
      return value as ClaimContinuation
    storage.removeItem(CLAIM_CONTINUATION_KEY)
  } catch {
    // Storage failure cannot silently turn a claim into a generic sign-in flow.
  }
  return null
}

export function clearClaimContinuation(storage: StoragePort = currentStorage()): void {
  try {
    storage.removeItem(CLAIM_CONTINUATION_KEY)
  } catch {
    /* tab state only */
  }
}

/** Shorten to the server's window; never extend a captured attempt on reload. */
export function capClaimContinuation(
  expiresAt: string,
  storage: StoragePort = currentStorage(),
): void {
  const value = readClaimContinuation(storage)
  const serverExpiry = Date.parse(expiresAt)
  if (!value || !Number.isFinite(serverExpiry)) return
  try {
    storage.setItem(
      CLAIM_CONTINUATION_KEY,
      JSON.stringify({
        ...value,
        expiresAt: Math.min(value.expiresAt, serverExpiry),
      }),
    )
  } catch {
    /* the current page retains its original bounded attempt */
  }
}

/** Must run before imports that may initialize auth, attribution or analytics.
 * Query secrets are rejected and scrubbed. The first-party verifier emits a
 * fragment which never reaches an HTTP request or Referer header. */
export function prepareClaimEntry(browser: ClaimWindow, now = Date.now()): boolean {
  let storage: StoragePort = unavailableStorage
  try {
    storage = browser.sessionStorage
  } catch {
    /* blocked browser storage fails closed */
  }
  const isClaim = browser.location.pathname === '/agent-claim'
  if (isClaim) {
    try {
      storage.setItem(CLAIM_ISOLATION_KEY, '1')
    } catch {
      /* route itself stays isolated */
    }
    const url = new URL(browser.location.href)
    const token = new URLSearchParams(url.hash.slice(1)).get('claim_attempt_token')
    // Drop all query/hash input before any module can read location.
    const language = siteLocale(url.searchParams.get('lang'))
    const clean = language
      ? `/agent-claim?${new URLSearchParams({ lang: language })}`
      : '/agent-claim'
    browser.history.replaceState(null, '', clean)
    if (token !== null) {
      clearClaimContinuation(storage)
      if (ATTEMPT.test(token)) {
        try {
          storage.setItem(
            CLAIM_CONTINUATION_KEY,
            JSON.stringify({
              attemptToken: token,
              expiresAt: now + CLAIM_ATTEMPT_TTL_MS,
            }),
          )
        } catch {
          /* fail closed: render restart instructions */
        }
      }
    }
  }
  let claimAuth = false
  if (AUTH_PATHS.has(browser.location.pathname)) {
    // Keep the auth return isolated even when the attempt expired while the
    // person was signing in; expiration must render restart, not analytics.
    try {
      claimAuth = storage.getItem(CLAIM_ISOLATION_KEY) === '1'
    } catch {
      /* no continuation */
    }
  } else if (!isClaim) {
    clearClaimContinuation(storage)
    try {
      storage.removeItem(CLAIM_ISOLATION_KEY)
    } catch {
      /* tab state only */
    }
  }
  readClaimContinuation(storage, now)
  const isolated = isClaim || claimAuth
  if (isolated) {
    let policy = browser.document.querySelector<HTMLMetaElement>('meta[name="referrer"]')
    if (!policy) {
      policy = browser.document.createElement('meta')
      policy.name = 'referrer'
      browser.document.head.append(policy)
    }
    policy.content = 'no-referrer'
  }
  return isolated
}
